import { GameConfig } from "@/config/GameConfig";

/**
 * A non-authoritative display cache only - lets the pre-run screen paint
 * something instantly on a repeat visit before the real network refresh
 * (`DailyChallengeClient.getMyStatus()`) resolves. Never trusted to
 * decide whether an attempt can be started, never used as an offline
 * fallback attempt count - see the PR description's "backend as the
 * authoritative attempt counter" rule. Always overwritten by the next
 * real server response; reloading the page never "restores" attempts
 * from here, because nothing here is ever read as ground truth in the
 * first place.
 */
export interface DailyLocalSnapshot {
  challengeId: string;
  attemptsRemaining: number;
  bestScore: number | null;
  bestCompleted: boolean;
}

function isValid(value: unknown): value is DailyLocalSnapshot {
  if (!value || typeof value !== "object") return false;
  const c = value as Partial<DailyLocalSnapshot>;
  return (
    typeof c.challengeId === "string" &&
    typeof c.attemptsRemaining === "number" &&
    (c.bestScore === null || typeof c.bestScore === "number") &&
    typeof c.bestCompleted === "boolean"
  );
}

export const DailyLocalState = {
  get(): DailyLocalSnapshot | null {
    try {
      const raw = globalThis.localStorage?.getItem(GameConfig.storage.dailyUiKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return isValid(parsed) ? parsed : null;
    } catch {
      return null;
    }
  },

  save(snapshot: DailyLocalSnapshot): void {
    try {
      globalThis.localStorage?.setItem(GameConfig.storage.dailyUiKey, JSON.stringify(snapshot));
    } catch {
      // Display cache only - safe to just skip on storage failure.
    }
  },

  /** QA-only: clears the display cache. Never affects the authoritative backend state. */
  clear(): void {
    try {
      globalThis.localStorage?.removeItem(GameConfig.storage.dailyUiKey);
    } catch {
      // Ignore.
    }
  },
};
